import React from 'react';
import {
  Alert,
  Box,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
} from '@mui/material';
import { Dispatch } from '@reduxjs/toolkit';
import {
  setDisplayContext,
  setLeaderLayout,
  setSettings,
} from '../../../../support/slices/tileBuilderSlice';
import {
  getContextDescription,
  getContextDisplayName,
  TileDisplayContext,
} from '../../../../support/tileDisplayContextUtils';

interface DisplayContextControlsProps {
  chartId: string;
  displayContext: TileDisplayContext;
  settings: any;
  leaderLayout?: {
    chartAreaWidth: number;
    chartAreaHeight: number;
  };
  dispatch: Dispatch;
}

const DisplayContextControls: React.FC<DisplayContextControlsProps> = ({
                                                                         chartId,
                                                                         displayContext,
                                                                         settings,
                                                                         leaderLayout,
                                                                         dispatch,
                                                                       }) => {
  return (
    <Box display="flex" flexDirection="column" gap={2}>
      <FormControl sx={{ minWidth: 320 }}>
        <InputLabel>Display Context</InputLabel>
        <Select
          value={displayContext}
          label="Display Context"
          onChange={(e) =>
            dispatch(
              setDisplayContext({
                chartId,
                displayContext: e.target.value as TileDisplayContext,
              })
            )
          }
        >
          <MenuItem value="preview">{getContextDisplayName('preview')}</MenuItem>
          <MenuItem value="dashboard">{getContextDisplayName('dashboard')}</MenuItem>
          <MenuItem value="leader">{getContextDisplayName('leader')}</MenuItem>
        </Select>
      </FormControl>

      <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
        {getContextDescription(displayContext)}
      </Typography>

      {displayContext === 'leader' && (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <Alert severity="info">
            <Typography variant="body2">
              Leader context shows exact layout structure from your Leader pages.
              Preview matches pixel-perfect dimensions including all padding and margins.
            </Typography>
          </Alert>

          <FormControl sx={{ minWidth: 200 }}>
            <InputLabel>Leader Page Type</InputLabel>
            <Select
              value={settings.leaderStripCount || 1}
              label="Leader Page Type"
              onChange={(e) => {
                const count = Number(e.target.value);
                const height = count === 1 ? 300 : 400;

                dispatch(
                  setSettings({
                    chartId,
                    settings: {
                      leaderStripCount: count,
                      leaderPreviewHeight: height,
                    } as any,
                  })
                );
                dispatch(setLeaderLayout({ chartId }));
              }}
            >
              <MenuItem value={1}>Individual (1 tile, 300px)</MenuItem>
              <MenuItem value={3}>Team (3 tiles, 400px)</MenuItem>
              <MenuItem value={2}>Custom: 2 Tiles</MenuItem>
              <MenuItem value={4}>Custom: 4 Tiles</MenuItem>
              <MenuItem value={5}>Custom: 5 Tiles</MenuItem>
            </Select>
          </FormControl>

          <TextField
            label="Container Height"
            type="number"
            value={settings.leaderPreviewHeight || 300}
            onChange={(e) => {
              dispatch(
                setSettings({
                  chartId,
                  settings: { leaderPreviewHeight: Number(e.target.value) } as any,
                })
              );
              dispatch(setLeaderLayout({ chartId }));
            }}
            inputProps={{ min: 250, max: 600, step: 25 }}
            sx={{ minWidth: 200 }}
            helperText="Height of the StbAccordion content area"
          />

          {leaderLayout && (
            <Alert severity="success" sx={{ mt: 1 }}>
              <Typography variant="caption" sx={{ display: 'block', fontWeight: 600 }}>
                Final Chart Rendering Area: {leaderLayout.chartAreaWidth}px ×{' '}
                {leaderLayout.chartAreaHeight}px
              </Typography>
              <Typography variant="caption" sx={{ display: 'block' }}>
                This matches exactly what DataVisualizationComponent will receive in the
                Leader pages.
              </Typography>
            </Alert>
          )}
        </Box>
      )}
    </Box>
  );
};

export default DisplayContextControls;